import React, { useState, useEffect, useRef } from 'react';
import { connect } from 'react-redux';
import { loadDormantDevicesReport } from '../../../redux/actions/reportsActions';
import InlineHelpText from '../../shared/InlineHelpText';
import SvgOpenExternal from '../../svg/OpenExternal';
import HelpIcon from '../../shared/HelpIcon';
import Table from '../../shared/table/Table';
import AccessControl from '../../../AccessControl';
import {
  FormattedDate,
  FormattedMessage,
  FormattedRelativeTime,
  FormattedTime,
  useIntl,
} from 'react-intl';
import permissions from '../../../permissionsList';
import {
  descriptionText,
  lastCheckinText,
  searchText,
  categoryText,
} from '../../shared/SharedInternationalizations';
import LoadingSpinner from '../../shared/LoadingSpinner';
import Pager from '../../shared/Pager';
import { createCSV, intlDatetimeForCsv  } from '../../../utilities/general';
import Button, {
  buttonTypes,
  buttonSizes,
  buttonTextSizes,
} from '../../shared/Button';

export function UnconnectedDormantDevices(props) {
  const { dormantDevices, alarmDeviceTypes, apiSpinner } = props;
  useEffect(() => {
    props.dispatch(loadDormantDevicesReport());
  }, []); // eslint-disable-line

  //Access the Intl API for formatting dates into CSV file
  const intl = useIntl();

  // setting up state and function for the pager
  const [pagerParams, setPagerParams] = useState({
    totalLength: 0,
    startValue: 0,
    endValue: 49,
    incrementValue: 50,
  });
  const [devicesFilter, setDevicesFilter] = useState('');

  function setPager(startValue) {
    const pagerValues = { ...pagerParams };
    let start = startValue;
    if (start <= 0) {
      pagerValues.startValue = 0;
      pagerValues.endValue = pagerParams.incrementValue - 1;
    } else {
      pagerValues.startValue = start;
      pagerValues.endValue = startValue + pagerParams.incrementValue - 1;
    }
    setPagerParams(pagerValues);
  }

  const devicesSearchFilter = (event) => {
    const value = event.target.value;
    const pagerValues = { ...pagerParams };
    pagerValues.startValue = 0;

    setPagerParams(pagerValues);
    setDevicesFilter(value);
  };

  const [showDormantDevicesHelpText, setShowDormantDevicesHelpText] = useState(
    false
  );
  const dormantDevicesHelpTextRef = useRef();
  function toggleDormantDevicesHelpText(value) {
    setShowDormantDevicesHelpText(value);
  }

  const dormantDevicesHelpText = (
    <FormattedMessage
      id="dormantDevices.dormantDevicesHelpText"
      defaultMessage="These are alarm devices that are dormant."
      description="Help text explaining who the assigned users are"
    />
  );


  const csvHeaders = ['Id', 'Description', 'Category', 'Last Checkin', 'Serial Id'];

  let csvData = [];

  const alarmDevicesTable = () => {
    const alarmDeviceDetailsList = [];
    dormantDevices.map((device) => {
      const deviceLink = (
        <AccessControl
          data-test="access-control"
          feature={permissions.static.devices.alarm.editAlarmDevice}>
          <FormattedMessage
            id="devicesReports.viewDeviceText"
            defaultMessage="View Device"
            description="Button to view full unit info">
            {(txt) => (
              <div className="width-100 flex-end height-100 align-center">
                <a
                  data-test="view-alarm-device-link"
                  className="text-small margin-right-10 link-text align-center"
                  href={`/admin/alarm_dev_edit_new.php?edit_row[alarm_device_id]=${device.alarm_device_id}&edit_row[alarm_device_type]=${device.alarm_device_type}&orig_type= 1`}>
                  {txt} <SvgOpenExternal className="margin-left-5" />
                </a>
              </div>
            )}
          </FormattedMessage>
        </AccessControl>
      );

      alarmDeviceDetailsList.push({
        id: device.alarm_device_id,
        description: device.alarm_description,
        type: device.alarm_device_type,
        view: deviceLink,
        lastCheckIn: device.last_check_in,
        serialId: device.serial_id,
      });

      return '';
    });

    // map over the units and put into the format that is required for the table component
    if (alarmDeviceDetailsList.length > 0) {
      const devicesArray = alarmDeviceDetailsList.map((d) => {
        let deviceType = '';
        if (alarmDeviceTypes[d.type]) {
          deviceType = alarmDeviceTypes[d.type].description;
        }
        const checkinGap = ((new Date() - new Date(d.lastCheckIn)) / 1000) * -1; // converts the difference to a negative number for the relative time calculation

        const lastCheckInFormatted = intlDatetimeForCsv( new Date(d.lastCheckIn), intl)
        csvData.push([d.id, d.description, deviceType, lastCheckInFormatted, d.serialId]);
        return {
          description: {
            sortBy: d.description.toUpperCase(),
            display: d.description,
          },
          type: {
            sortBy: deviceType.toUpperCase(),
            display: deviceType,
          },
          last_checkin: {
            sortBy: new Date(d.lastCheckIn),
            display: (
              <React.Fragment>
                <FormattedDate
                  value={new Date(d.lastCheckIn)}
                  year="numeric"
                  month="long"
                  day="2-digit"
                />
                <span className="margin-left-10"></span>
                <FormattedTime value={new Date(d.lastCheckIn)} />
                <br></br>
                <FormattedRelativeTime
                  value={checkinGap}
                  updateIntervalInSeconds={60}
                />
              </React.Fragment>
            ),
          },
          view: {
            sortBy: '',
            display: d.view,
          },
        };
      });
      // Take the array and filter based on the text entered in the search bar.
      let searchedDevices = [];
      // Take the array and filter based on the text entered in the search bar.
      if (devicesFilter !== '') {
        searchedDevices = devicesArray.filter((item) => {
          let filterResult = false;
          // Make the device name uppercase for case insensitivity
          const description = item.description.display.toUpperCase();
          const type = item.type.display.toUpperCase();

          // filter based off the includes() value with the entered text (also switched to uppercase) as the filter parameter
          if (
            description.includes(devicesFilter.toUpperCase()) ||
            type.includes(devicesFilter.toUpperCase())
          ) {
            filterResult = true;
          }
          return filterResult;
        });
      } else {
        searchedDevices = devicesArray;
      }
      // Take the array and filter based on the text entered in the search bar.
      if (searchedDevices.length !== pagerParams.totalLength) {
        const pagerValues = { ...pagerParams };
        pagerValues.totalLength = searchedDevices.length;
        setPagerParams(pagerValues);
      }

      let pagedDevices = [];
      searchedDevices.map((d, index) => {
        if (index >= pagerParams.startValue && index <= pagerParams.endValue) {
          pagedDevices.push(d);
        }
        return '';
      });
      const tableHeadings = [
        {
          isSortable: true,
          sortBy: 'description',
          display: descriptionText,
        },
        {
          isSortable: true,
          sortBy: 'type',
          display: categoryText,
        },
        {
          isSortable: true,
          sortBy: 'last_checkin',
          display: lastCheckinText,
        },
        {
          isSortable: false,
          sortBy: 'view',
          display: '',
        },
      ];

      // generates the actual table
      const table = (
        <Table
          hasTableHead={true}
          hasSelector={false}
          tableHeadings={tableHeadings}
          sortParam="description"
          tableData={pagedDevices}
          tableStyles="table-layout__4-column text-small"
          data-test="dormant-devices-table"
        />
      );
      return table;
    } else {
      return (
        <span className="text-dimmed margin-right-10">
          <FormattedMessage
            data-test="no-dormant-alarm-devices"
            id="dormantDevices.noDevices"
            defaultMessage="No Dormant Devices Found"
            description="The user's contact phone number"
          />
        </span>
      );
    }
  };

  function tableDisplay() {
    if (dormantDevices.length > 0 || apiSpinner.dormantDevicesReportReturned) {
      return alarmDevicesTable();
    } else {
      return (
        <div className="loading-spinner__user-list-container">
          <LoadingSpinner />
        </div>
      );
    }
  }

  function triggerCSV() {
    return createCSV(csvHeaders, csvData, 'dormant_devices');
  }

  return (
    <React.Fragment>
      <div className="item-container__main-text text-large text-light flex-spread margin-bottom-10">
        <div className="align-center">
          <FormattedMessage
            id="dormantDevices.dormantDevicesText"
            defaultMessage="Dormant Devices Report:"
            description="The assigned alert devices for this unit"
          />
        </div>
        <div className="align-center">
          <HelpIcon
            onClick={() => dormantDevicesHelpTextRef.current.toggleHelpText()}
            toggled={showDormantDevicesHelpText}
            styles="margin-left-5"
          />
        </div>
      </div>
      <InlineHelpText
        ref={dormantDevicesHelpTextRef}
        toggleFunction={toggleDormantDevicesHelpText}
        message={dormantDevicesHelpText}
      />
      <div className="table__header padding-10 flex-spread">
        <div className="vertical-center">
          <label className="text-medium text-bold" htmlFor="usersFilter">
            {searchText}:
          </label>
          <input
            type="text"
            id="usersFilter"
            name="usersFilter"
            onChange={devicesSearchFilter}
            className="message-table__filter-search-box margin-left-10"
            value={devicesFilter}
          />{' '}
        </div>
        <Button
          onClick={triggerCSV}
          text="CSV"
          buttonType={buttonTypes.secondaryReversed}
          buttonSize={buttonSizes.small}
          buttonTextSize={buttonTextSizes.extraSmall}
        />
      </div>
      {tableDisplay()}
      {pagerParams.totalLength > 0 ? (
        <Pager
          totalLength={pagerParams.totalLength}
          startValue={pagerParams.startValue}
          incrementValue={pagerParams.incrementValue}
          onClick={setPager}
        />
      ) : (
        ''
      )}
    </React.Fragment>
  );
}

const mapStateToProps = (state, ownProps) => {
  return {
    dormantDevices: state.reports.devices.dormant,
    alarmDeviceTypes: state.devices.alarmDeviceTypes,
    apiSpinner: state.ui.apiSpinner,
  };
};

export default connect(mapStateToProps)(UnconnectedDormantDevices);
